import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { useGlobalStore } from '../stores/globalStore';
import { entrySection, locate, type NavEntry, type NavSectionConfig } from './navConfig';

/**
 * Which section a page listed in two sections was opened from (Jeff, 2026-10-24: Selective Touch
 * under Residents as well as Kitchen).
 *
 * The sidebar remembers the section of the panel link the user clicked. While they stay on that page
 * (its own sub-addresses included), the rail, panel and breadcrumb show that section; once they go
 * to any other page the memory is dropped. Arriving any other way (search, a Kitchen card, a pin, a
 * bookmark, a reload) finds nothing remembered, so the page shows in its home section (locate).
 * In memory only: nothing to go stale between visits.
 */
interface NavEntryState {
  entry: NavEntry | null;
  setEntry: (entry: NavEntry | null) => void;
}

export const useNavEntryStore = create<NavEntryState>((set) => ({
  entry: null,
  setEntry: (entry) => set({ entry }),
}));

/** Called by a sidebar panel link as it is clicked: `key` is the page's config route. */
export function rememberNavEntry(key: string, sectionId: string): void {
  useNavEntryStore.getState().setEntry({ key, sectionId });
}

/**
 * The section the current page was opened from, when that is not its home section; null otherwise.
 * Also drops the remembered entry once the user is on a different page.
 */
export function useNavEntrySection(): { pageKey: string; section: NavSectionConfig } | null {
  const { pathname } = useLocation();
  const facilityId = useGlobalStore((s) => s.facilityId);
  const entry = useNavEntryStore((s) => s.entry);
  const pageKey = locate(pathname, facilityId)?.page?.route ?? null;

  // Only on a change of page: the click that sets the entry also moves to the page it names, so the
  // entry is never dropped on its way in.
  useEffect(() => {
    const { entry: current, setEntry } = useNavEntryStore.getState();
    if (current && current.key !== pageKey) setEntry(null);
  }, [pageKey]);

  return useMemo(() => {
    const section = entrySection(pageKey, entry);
    return section && pageKey ? { pageKey, section } : null;
  }, [pageKey, entry]);
}
